import React from "react";
import { useTranslation } from "react-i18next";
import { Check, Sparkles } from "lucide-react";
import type { BillingInterval, Plan } from "../../../../shared/types.js";
import { PlanPriceLines } from "./PlanComparisonTable.js";

const PLAN_ORDER: Plan[] = ["basic", "premium"];

/**
 * The plan choice on the plans page: one card per plan, the whole card is the button. In
 * September most dead clicks on this page landed on a plan's name, price or seal, which only the
 * small "Escolher" button used to answer.
 */
export function PlanChoiceCards({
  interval,
  lockedPlan,
  offerFirstCents,
  onChoose,
}: {
  interval: BillingInterval;
  /** Offer path: only this plan is on sale (Premium at the Basic price). */
  lockedPlan?: Plan;
  /** First-month offer: both plans charge this amount today. */
  offerFirstCents?: number;
  onChoose: (plan: Plan) => void;
}) {
  const plans = lockedPlan ? PLAN_ORDER.filter((plan) => plan === lockedPlan) : PLAN_ORDER;
  return (
    <div className={`plan-choice-grid ${plans.length === 1 ? "plan-choice-grid-single" : ""}`}>
      {plans.map((plan) => (
        <PlanChoiceCard
          key={plan}
          plan={plan}
          interval={interval}
          offerFirstCents={offerFirstCents}
          onChoose={() => onChoose(plan)}
        />
      ))}
    </div>
  );
}

function PlanChoiceCard({
  plan,
  interval,
  offerFirstCents,
  onChoose,
}: {
  plan: Plan;
  interval: BillingInterval;
  offerFirstCents?: number;
  onChoose: () => void;
}) {
  const { t } = useTranslation();
  const featured = plan === "premium";
  const planLabel = t(`plans.${plan}`);
  return (
    <button
      type="button"
      // On a phone Premium comes first and full width; Basic folds into one compact row below it.
      className={`plan-card plan-card-selectable plan-choice-card ${featured ? "plan-card-featured" : "plan-choice-card-compact"}`}
      onClick={onChoose}
    >
      {featured && (
        <span className="plan-comparison-seal">
          <Sparkles className="plan-comparison-seal-icon" aria-hidden />
          {t("plans.recommended")}
        </span>
      )}
      <span className="plan-choice-head">
        <span className="plan-comparison-plan-name">{planLabel}</span>
        <PlanPriceLines plan={plan} interval={interval} offerFirstCents={offerFirstCents} installmentsFirst />
      </span>
      {featured && (
        <ul className="plan-choice-highlights">
          {(t("plansPage.premiumHighlights", { returnObjects: true }) as unknown as string[]).map((line) => (
            <li key={line}>
              <Check className="plan-choice-highlight-icon" aria-hidden />
              {line}
            </li>
          ))}
        </ul>
      )}
      <span
        className={`plan-comparison-cta plan-choice-cta ${featured ? "plan-comparison-cta-primary" : "plan-comparison-cta-secondary"}`}
      >
        {t("planComparison.chooseShort", { plan: planLabel })}
      </span>
    </button>
  );
}
